import { LabelledToggle } from '../Toggle';
import { Table } from '../Table';
import styled, { useTheme } from 'styled-components';
import type { LeaderboardUser, LeaderboardDepartment } from '../../lib/types/LeaderboardUser';
import {
  type LeaderboardPagedResults,
  LeaderboardTypeParam,
  useLeaderboardQuery,
} from '../../apiHooks/useLeaderboardQuery';
import { useLeaderboardWidgetData } from './hooks/useLeaderboardWidgetData';
import { useEffect, useState, type FC } from 'react';
import { Card } from '../Card';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { useMeQuery } from '../../apiHooks/useMeQuery';
import { NoLeaderboardDataCard } from './components/NoLeaderboardDataCard';
import { LeaderboardExpanded } from './LeaderboardExpanded';
import { useCurrentUserDepartment } from './hooks/useCurrentUserDepartment';

type LeaderboardWidgetUIProps = {
  isManager?: boolean;
};

export const LeaderboardWidgetUI: FC<LeaderboardWidgetUIProps> = ({ isManager = false }) => {
  const [selectedScopeFilter, setSelectedScopeFilter] = useState<LeaderboardTypeParam | undefined>(
    LeaderboardTypeParam.TEAM,
  );
  const { columns, scopeFilterLabels } = useLeaderboardWidgetData(isManager, selectedScopeFilter);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const theme = useTheme();

  const { data: meData } = useMeQuery();
  const currentUserDepartment = useCurrentUserDepartment();
  const {
    data: leaderboardWidgetData,
    isFetching,
    refetch,
  } = useLeaderboardQuery({
    type: selectedScopeFilter || LeaderboardTypeParam.TEAM,
    mode: 'top',
    count: 5,
    isManager,
  });

  useEffect(() => {
    refetch();
  }, [selectedScopeFilter, refetch]);

  useEffect(() => {
    if (!isFetching) {
      setIsLoading(false);
    }
  }, [isFetching]);

  const getTableData = (
    data:
      | (LeaderboardUser | LeaderboardDepartment)[]
      | LeaderboardPagedResults<LeaderboardUser | LeaderboardDepartment>
      | undefined,
  ): (LeaderboardUser | LeaderboardDepartment)[] => {
    if (Array.isArray(data)) {
      return data;
    }
    return data?.values || [];
  };

  const tableData = getTableData(leaderboardWidgetData);
  const isScopeToggleDisabled = isLoading || isFetching;

  const onScopeFilterChange = (index: number) => {
    if (isScopeToggleDisabled) {
      return;
    }

    const nextScope = scopeFilterLabels[index]?.value;

    if (!nextScope || nextScope === selectedScopeFilter) {
      return;
    }

    setSelectedScopeFilter(nextScope);
    setIsLoading(true);
  };

  return (
    <>
      <Card
        withShadow
        withBorder
        borderRadius={12}
        title={
          <CardTitleContainer>
            <NamedContentEntryText container={'span'} refKey="sectionTitles" subKey="leaderboard" />
            <ViewMoreButton
              type="button"
              aria-label="View more leaderboard entries"
              onClick={() => setShowDialog(true)}
            >
              View more
            </ViewMoreButton>
          </CardTitleContainer>
        }
      >
        <FiltersBar>
          <LabelledToggle
            fullWidth
            labels={scopeFilterLabels}
            disabled={isScopeToggleDisabled}
            onSelect={(selectedIndex: number) => onScopeFilterChange(selectedIndex)}
          />
        </FiltersBar>
        <Table
          withRowGap
          data={tableData}
          columns={columns}
          hideHeaders
          roundedRows
          hideSeparator
          rowColorSelector={(data: LeaderboardUser | LeaderboardDepartment) => {
            // Highlight current user in team/company leaderboards
            if ('learnerId' in data) {
              return data.learnerId === meData?.learnerId
                ? theme.colors.prototype.accent.replace(')', '/10%)')
                : 'unset';
            }
            // Highlight current user's department in department leaderboard
            if ('department' in data && currentUserDepartment) {
              return data.department === currentUserDepartment
                ? theme.colors.prototype.accent.replace(')', '/10%)')
                : 'unset';
            }
            return 'unset';
          }}
          renderZeroState={() => <NoLeaderboardDataCard />}
          isLoading={isLoading || isFetching}
        />
      </Card>
      {showDialog && (
        <LeaderboardExpanded
          isManager={isManager}
          isOpen={showDialog}
          initialTab={selectedScopeFilter}
          onClose={() => setShowDialog(false)}
        />
      )}
    </>
  );
};

const FiltersBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitleContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const ViewMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.accentText};

  &:hover {
    text-decoration: underline;
  }
`;
